import React, { useState, useRef } from 'react'
import { useField, useFormApi, useFormState } from 'informed'
import set from 'lodash/set'
import { useTranslation } from 'react-i18next'
import isIP from 'validator/lib/isIP'
import produce from 'immer'

import Button from 'components/Button'
import { Select, Text } from 'components/forms'
import { Delete } from 'media/icons'

import { RoomSelector } from '../../CashRegister/Kasse'
import {
	DeletePrinterButton,
	PrinterActions,
	PrinterList,
	PrinterListTitle,
	ReceiptPrinterFields,
	ReceiptPrinterHeader,
	ReceiptPrinterRow
} from './styled'

const MAX_RECEIPT_PRINTERS = 10

const PrinterListFields = ({ index, designProject, printer, hardwareIdList, printerOptions, isOnlyOneRecord, isLastRecord, handleRemoveRecord }) => {
	const [t] = useTranslation()

	const fieldPrefix = `parameters.printer.receiptPrintersConfiguration[${index}]`
	const isDefaultPrinter = index === 0
	const doWeShowPrinterIp = printer?.type &&
		(printer.type.indexOf('SUNMI') === -1 || printer.type === 'SUNMI Cloud Printer') &&
		printer.type !== 'CCV A920'


	const showRoom = printer?.type === 'SUNMI Cloud Printer' && !isDefaultPrinter
	const columnCount = 1 + (doWeShowPrinterIp ? 1 : 0) + (showRoom ? 1 : 0)

	return (
		<ReceiptPrinterRow>
			<ReceiptPrinterHeader>
				<span>{t('options_section.fields.printer_number', { value: index + 1 })}</span>
				{isLastRecord && !isOnlyOneRecord &&
					<DeletePrinterButton
						type='button'
						title={t('options_section.strings.delete')}
						onClick={event => {
							event.preventDefault()
							handleRemoveRecord(index)
						}}
					>
						<Delete />
					</DeletePrinterButton>
				}
			</ReceiptPrinterHeader>
			<ReceiptPrinterFields columns={columnCount}>
				<Select
					field={`${fieldPrefix}.type`}
					label={t('options_section.fields.printer_type')}
					options={printerOptions}
					isClearable={isOnlyOneRecord}
					simpleValue
					validate={v => !isOnlyOneRecord && !v ? t('validators.required') : null}
					validateOnChange
				/>
				{doWeShowPrinterIp && (
					<Text
						field={`${fieldPrefix}.connectionTarget`}
						label={printer?.type?.indexOf('SUNMI') !== -1
							? t('options_section.strings.serial_number_or_ip')
							: t('options_section.strings.ip_address')}
						validate={(v) => {
							if(!printer?.type) {
								return null
							}
							if(!v) {
								return t('validators.required')
							}
							const valueIsIp = isIP(v)
							if(printer?.type.indexOf('EPSON') !== -1 && !valueIsIp) {
								return t('validators.not_a_valid_ip_address')
							}
							const valueIsValidHWID = hardwareIdList.includes(v)
							if(printer?.type.indexOf('SUNMI') !== -1 && !(valueIsIp || valueIsValidHWID)) {
								return t('validators.ipaddress_or_registered_hwid')
							}
						}}
					/>
				)}
				{showRoom && (
					<RoomSelector
						field={`${fieldPrefix}.roomIds`}
						label={t('options_section.strings.preset_room')}
						designProjectId={designProject}
						isMulti
					/>
				)}
			</ReceiptPrinterFields>
		</ReceiptPrinterRow>
	)
}

const MobileWaiterReceiptPrinters = ({ formData, printerOptions, hardwareIdList, designProject }) => {
	const state = useFormState()
	const formApi = useFormApi()
	const printerListField = 'parameters.printer.receiptPrintersConfiguration'
	useField({ field: printerListField })

	const initialCount = Math.min(
		formData?.parameters?.printer?.receiptPrintersConfiguration?.length || 1,
		MAX_RECEIPT_PRINTERS
	)
	const [printerFieldCount, setPrinterFieldCount] = useState(initialCount)
	const nextKey = useRef(initialCount)
	const [keys] = useState(() => Array.from({ length: initialCount }, (_, i) => i))

	const [t] = useTranslation()

	const handleRemoveRecord = index => {
		const stateValues = formApi.getValues()
		const newReceiptPrintersConfiguration = (stateValues.parameters?.printer?.receiptPrintersConfiguration || []).filter((_, i) => i !== index)
		const newValues = produce(stateValues, draft => {
			set(draft, printerListField, newReceiptPrintersConfiguration)
			return draft
		})
		formApi.setInitialValue(printerListField, newReceiptPrintersConfiguration)
		formApi.setValues(newValues)
		keys.splice(index, 1)
		setPrinterFieldCount(count => count - 1)
		formApi.submitForm()
	}

	const handleAddRecord = () => {
		const lastPrinter = state.values?.parameters?.printer?.receiptPrintersConfiguration?.[printerFieldCount - 1]
		if(!lastPrinter?.type) {
			return
		}
		keys.push(nextKey.current + 1)
		setPrinterFieldCount(count => count + 1)
	}

	return (
		<>
			<PrinterListTitle>{t('options_section.fields.printer_list')}</PrinterListTitle>
			<PrinterList>
				{Array.from({ length: printerFieldCount }).map((_, index) => (
					<PrinterListFields
						key={keys[index]}
						index={index}
						printer={state.values?.parameters?.printer?.receiptPrintersConfiguration?.[index]}
						printerOptions={printerOptions}
						hardwareIdList={hardwareIdList}
						designProject={designProject}
						isOnlyOneRecord={printerFieldCount === 1}
						isLastRecord={index === printerFieldCount - 1}
						handleRemoveRecord={handleRemoveRecord}
					/>
				))}
			</PrinterList>
			<PrinterActions>
				<Button
					text={t('options_section.fields.add_printer')}
					status={printerFieldCount < MAX_RECEIPT_PRINTERS ? 'primary' : 'inactive'}
					shrink
					disabled={printerFieldCount >= MAX_RECEIPT_PRINTERS}
					onClick={handleAddRecord}
				/>
			</PrinterActions>
		</>
	)
}

export default MobileWaiterReceiptPrinters
